import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Pencil, ExternalLink } from 'lucide-react';
import Badge from '@/components/ui/Badge';
import Card from '@/components/ui/Card';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import Button from '@/components/ui/Button';
import ScheduleFormModal from '@/components/admin/ScheduleFormModal';
import api from '@/lib/api';
import { useUIStore } from '@/stores/ui-store';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AgentSchedule {
  id: string;
  name: string;
  description: string | null;
  task_type: string;
  cron_expression: string;
  cron_human: string | null;
  is_active: boolean;
  created_by: string;
  last_run_at: string | null;
  last_run_status: string | null;
  next_run_at: string | null;
  run_count: number;
  context: Record<string, unknown> | null;
  created_at: string;
  recent_runs?: ScheduleRun[];
}

interface ScheduleRun {
  id: string;
  task_id: string | null;
  status: string;
  started_at: string;
  completed_at: string | null;
  duration_ms: number | null;
  result_summary: string | null;
  cost_usd: number | null;
  error: string | null;
}

interface RunsResponse {
  runs: ScheduleRun[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const taskTypeBadge: Record<string, { variant: 'blue' | 'green' | 'yellow' | 'red' | 'gray'; label: string }> = {
  supplier_discovery: { variant: 'blue', label: 'Supplier Discovery' },
  catalog_curation: { variant: 'green', label: 'Catalog Curation' },
  pricing_review: { variant: 'yellow', label: 'Pricing Review' },
  order_fulfillment: { variant: 'red', label: 'Order Fulfillment' },
  catalog_sync: { variant: 'gray', label: 'Catalog Sync' },
  supplier_research: { variant: 'blue', label: 'Supplier Research' },
  product_listing: { variant: 'green', label: 'Product Listing' },
};

const runStatusBadge: Record<string, { variant: 'green' | 'red' | 'blue' | 'yellow' | 'gray'; label: string }> = {
  success: { variant: 'green', label: 'Success' },
  completed: { variant: 'green', label: 'Success' },
  failed: { variant: 'red', label: 'Failed' },
  running: { variant: 'blue', label: 'Running' },
  escalated: { variant: 'yellow', label: 'Escalated' },
};

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString();
}

function formatDuration(ms: number | null): string {
  if (ms === null) return '—';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const m = Math.floor(ms / 60_000);
  const s = Math.floor((ms % 60_000) / 1000);
  return `${m}m ${s}s`;
}

// ---------------------------------------------------------------------------
// Run history table
// ---------------------------------------------------------------------------

function RunHistoryTable({ runs, isLoading }: { runs: ScheduleRun[]; isLoading: boolean }) {
  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner />
      </div>
    );
  }

  if (runs.length === 0) {
    return (
      <div className="text-center py-10 text-gray-500">
        <p>No runs yet. The schedule will execute according to its cron expression.</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 text-left">
            <th className="px-4 py-3 font-medium text-gray-500">Status</th>
            <th className="px-4 py-3 font-medium text-gray-500">Started</th>
            <th className="px-4 py-3 font-medium text-gray-500">Duration</th>
            <th className="px-4 py-3 font-medium text-gray-500">Result Summary</th>
            <th className="px-4 py-3 font-medium text-gray-500 text-right">Cost</th>
            <th className="px-4 py-3 font-medium text-gray-500 text-right">Task</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {runs.map((run) => {
            const badge = runStatusBadge[run.status] ?? { variant: 'gray' as const, label: run.status };
            return (
              <tr key={run.id} className="hover:bg-gray-50 transition-colors">
                <td className="px-4 py-3">
                  <Badge variant={badge.variant}>{badge.label}</Badge>
                </td>
                <td className="px-4 py-3 text-gray-600">{formatDate(run.started_at)}</td>
                <td className="px-4 py-3 text-gray-600">{formatDuration(run.duration_ms)}</td>
                <td className="px-4 py-3 text-gray-600 max-w-xs truncate">
                  {run.result_summary ?? run.error ?? '—'}
                </td>
                <td className="px-4 py-3 text-right text-gray-600">
                  {run.cost_usd != null ? `$${run.cost_usd.toFixed(4)}` : '—'}
                </td>
                <td className="px-4 py-3 text-right">
                  {run.task_id ? (
                    <Link
                      to={`/admin/agent/tasks/${run.task_id}`}
                      className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      View
                    </Link>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function AgentScheduleDetail() {
  const { id } = useParams<{ id: string }>();
  const addToast = useUIStore((s) => s.addToast);

  const [schedule, setSchedule] = useState<AgentSchedule | null>(null);
  const [isLoadingSchedule, setIsLoadingSchedule] = useState(true);
  const [runs, setRuns] = useState<ScheduleRun[]>([]);
  const [isLoadingRuns, setIsLoadingRuns] = useState(false);
  const [runsPagination, setRunsPagination] = useState({ page: 1, limit: 20, total: 0, total_pages: 0 });
  const [showEditModal, setShowEditModal] = useState(false);

  const fetchSchedule = async () => {
    if (!id) return;
    try {
      setIsLoadingSchedule(true);
      const res = await api.get<AgentSchedule>(`/admin/agent/schedules/${id}`);
      setSchedule(res);
      if (res.recent_runs) {
        setRuns(res.recent_runs);
      }
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to load schedule', 'error');
    } finally {
      setIsLoadingSchedule(false);
    }
  };

  const fetchRuns = async (page = 1) => {
    if (!id) return;
    try {
      setIsLoadingRuns(true);
      const params = new URLSearchParams({ page: page.toString(), limit: '20' });
      const res = await api.get<RunsResponse>(`/admin/agent/schedules/${id}/runs?${params.toString()}`);
      setRuns(res.runs);
      setRunsPagination(res.pagination);
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to load run history', 'error');
    } finally {
      setIsLoadingRuns(false);
    }
  };

  useEffect(() => {
    fetchSchedule();
    fetchRuns(1);
  }, [id]);

  const handleSaved = (saved: AgentSchedule) => {
    setSchedule(saved);
  };

  if (isLoadingSchedule) {
    return (
      <div className="flex justify-center py-20">
        <LoadingSpinner />
      </div>
    );
  }

  if (!schedule) {
    return (
      <div className="text-center py-20">
        <p className="text-gray-500">Schedule not found.</p>
        <Link to="/admin/agent/schedules" className="mt-4 inline-block text-blue-600 hover:underline">
          Back to Schedules
        </Link>
      </div>
    );
  }

  const tt = taskTypeBadge[schedule.task_type] ?? { variant: 'gray' as const, label: schedule.task_type };

  return (
    <div className="space-y-6">
      {/* Back link */}
      <div>
        <Link
          to="/admin/agent/schedules"
          className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to Schedules
        </Link>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-gray-900">{schedule.name}</h1>
            <Badge variant={schedule.is_active ? 'green' : 'gray'}>
              {schedule.is_active ? 'Active' : 'Inactive'}
            </Badge>
          </div>
          {schedule.description && (
            <p className="text-gray-600 mt-1">{schedule.description}</p>
          )}
        </div>
        <Button onClick={() => setShowEditModal(true)}>
          <Pencil className="w-4 h-4 mr-2" />
          Edit Schedule
        </Button>
      </div>

      {/* Info card */}
      <Card padding="lg">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Schedule Details</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
          <div>
            <dt className="text-sm font-medium text-gray-500">Task Type</dt>
            <dd className="mt-1">
              <Badge variant={tt.variant}>{tt.label}</Badge>
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Created By</dt>
            <dd className="mt-1">
              <Badge variant={schedule.created_by === 'agent' ? 'yellow' : 'blue'}>
                {schedule.created_by === 'agent' ? 'Agent' : 'Admin'}
              </Badge>
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Cron Expression</dt>
            <dd className="mt-1 font-mono text-sm text-gray-800">{schedule.cron_expression}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Human-readable</dt>
            <dd className="mt-1 text-sm text-gray-800">{schedule.cron_human ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Last Run</dt>
            <dd className="mt-1 text-sm text-gray-800">{formatDate(schedule.last_run_at)}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Next Run</dt>
            <dd className="mt-1 text-sm text-gray-800">{formatDate(schedule.next_run_at)}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Total Runs</dt>
            <dd className="mt-1 text-sm text-gray-800">{schedule.run_count}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Created</dt>
            <dd className="mt-1 text-sm text-gray-800">{formatDate(schedule.created_at)}</dd>
          </div>
          {schedule.context && (
            <div className="col-span-2">
              <dt className="text-sm font-medium text-gray-500 mb-1">Context</dt>
              <dd>
                <pre className="text-xs font-mono bg-gray-50 border border-gray-200 rounded-md px-3 py-2 overflow-x-auto whitespace-pre-wrap">
                  {JSON.stringify(schedule.context, null, 2)}
                </pre>
              </dd>
            </div>
          )}
        </div>
      </Card>

      {/* Run history */}
      <Card padding="none">
        <div className="px-4 py-3 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Run History</h2>
        </div>

        <RunHistoryTable runs={runs} isLoading={isLoadingRuns} />

        {runsPagination.total_pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              {runsPagination.total} run{runsPagination.total !== 1 ? 's' : ''}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => fetchRuns(runsPagination.page - 1)}
                disabled={runsPagination.page <= 1}
                className="p-1.5 rounded text-gray-400 hover:text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-gray-600">
                {runsPagination.page} / {runsPagination.total_pages}
              </span>
              <button
                onClick={() => fetchRuns(runsPagination.page + 1)}
                disabled={runsPagination.page >= runsPagination.total_pages}
                className="p-1.5 rounded text-gray-400 hover:text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </Card>

      {/* Edit modal */}
      <ScheduleFormModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSaved={handleSaved}
        editingSchedule={schedule}
      />
    </div>
  );
}
